// components/ProductRows/ProductRowsTableView.tsx
import { alpha, Box, Chip, Grid, Typography, useTheme } from '@mui/material'
import { viewOnlyColumns, viewOnlyCsaColumns } from 'components/ContractDialog/columns'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import * as React from 'react'
import { NewDeal, Product } from 'types'
import { formatDateString } from 'utils/formatDayjsDateTime'

type Props = { deal?: NewDeal; productRows?: Product[]; showPointsView?: boolean }

const money = (v: any) =>
  Number.isFinite(Number(v)) ? Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' }) : '—'
const percent = (v: any) => (Number.isFinite(Number(v)) ? `${Number(v)}`.replace(/\.0+$/, '') + '%' : '—')

const getCols = (type?: string) => {
  const csa = ['CSA', 'RCSA']
  return type && csa.includes(type) ? viewOnlyCsaColumns : viewOnlyColumns
}

// Points-specific columns injected after productName for points deals
const pointsColumns = [
  {
    id: 'points',
    label: 'Points',
    type: 'number',
    flex: '0 0 70px',
    style: { background: 'none', border: 'none' },
    xs: 1,
    sm: 1,
    md: 1,
    lg: 1,
    xl: 0.6,
  },
  // { id: 'ratePerPoint', label: '$/pt', type: 'number', flex: '0 0 70px', style: { background: 'none', border: 'none' }, xs: 1, sm: 1, md: 1, lg: 1, xl: 0.6 },
]

// which edit columns to keep in view (same order)
const VISIBLE_IN_VIEW = new Set([
  'productName',
  'price',
  'billedPrice',
  'margin',
  'mediaSpend',
  'status',
  'dateRange',
  'payer',
  // 'accountUsed',
  'grossProfit',
])

export default function ProductRowsTableView({ deal, productRows = [], showPointsView }: Props) {
  const theme = useTheme()
  const isPoints = showPointsView ?? deal?.billingModel === 'points'
  const rowColumns = React.useMemo(() => {
    const base = getCols(deal?.type).filter(
      c => VISIBLE_IN_VIEW.has(c.id) && c.id !== 'deleteButton' && c.id !== 'description' && c.id !== 'billedPrice'
    )
    if (!isPoints) return base
    // Insert points columns after productName
    const nameIdx = base.findIndex(c => c.id === 'productName')
    const result = [...base]
    result.splice(nameIdx + 1, 0, ...pointsColumns)
    return result
  }, [deal?.type, isPoints])

  if (!productRows.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 120,
          borderRadius: 2,
          border: '2px dashed',
          borderColor: 'divider',
          opacity: 0.6,
          m: 1,
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          No services added
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 0.5 }}>
      {/* Column headers */}
      <Grid
        container
        sx={{
          alignItems: 'center',
          px: 1.5,
          py: 0.75,
          mx: 0.5,
          borderRadius: 1,
        }}
      >
        {rowColumns.map(col => (
          <Grid
            key={col.id}
            item
            //@ts-ignore
            xs={col.xs}
            //@ts-ignore
            sm={col.sm}
            //@ts-ignore
            md={col.md}
            //@ts-ignore
            lg={col.lg}
            //@ts-ignore
            xl={col.xl}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'text.secondary',
                ml: 0.5,
              }}
            >
              {col.label}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Rows */}
      {productRows.map((row, index) => (
        <Grid
          key={row.SK}
          container
          sx={{
            alignItems: 'center',
            py: 0.75,
            px: 1.5,
            mx: 0.5,
            borderRadius: 1.5,
            bgcolor: 'background.section',
            border: '1px solid transparent',
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: alpha(theme.palette.text.primary, 0.03),
              borderColor: 'divider',
            },
          }}
        >
          {rowColumns.map(col => (
            <Grid
              key={col.id}
              item
              //@ts-ignore
              xs={col.xs}
              //@ts-ignore
              sm={col.sm}
              //@ts-ignore
              md={col.md}
              //@ts-ignore
              lg={col.lg}
              //@ts-ignore
              xl={col.xl}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                px: 0.5,
              }}
            >
              {renderValue(col.id, col.label, row)}
            </Grid>
          ))}
        </Grid>
      ))}
    </Box>
  )
}

function renderValue(id: string, label: string, row: Product) {
  const numStyle = {
    fontSize: 13,
    fontVariantNumeric: 'tabular-nums' as const,
    textAlign: 'left' as const,
    width: '100%',
    fontWeight: 500,
  }

  switch (label) {
    case 'Points':
      return <Typography sx={numStyle}>{row.points != null ? Number(row.points) : '–'}</Typography>
    case '$/pt':
      return <Typography sx={numStyle}>{row.ratePerPoint != null ? money(row.ratePerPoint) : '–'}</Typography>
    case 'Price':
      return <Typography sx={numStyle}>{money(row.price)}</Typography>
    case 'Media Spend':
      return <Typography sx={numStyle}>{money(row.mediaSpend)}</Typography>
    case 'Gross Profit':
      return (
        <Typography sx={{ ...numStyle, color: Number(row.grossProfit) > 0 ? 'text.complete' : 'text.primary' }}>
          {money(row.grossProfit)}
        </Typography>
      )
    case 'Total':
      return <Typography sx={numStyle}>{money(row.total)}</Typography>
    case 'Margin':
      return <Typography sx={numStyle}>{percent(row.margin)}</Typography>
    case 'Qty':
      return <Typography sx={numStyle}>{Number(row.qty ?? 0)}</Typography>
    case 'Date Range': {
      const s = formatDateString(row.startDate)
      const e = formatDateString(row.endDate)
      return (
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          {s} — {e}
        </Typography>
      )
    }
    case 'Status':
      return (
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: renderColorByString(row.status) }}>
          {row.status || '—'}
        </Typography>
      )
    default:
      if (id === 'productName') {
        return (
          <>
            {row?.phase && row?.phase === 'setup' ? (
              <Box sx={{ display: 'inline-flex', gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: 'text.primary',
                  }}
                >
                  {row.productName || '—'}
                </Typography>
                <Chip
                  label={'Setup'}
                  size="small"
                  sx={{
                    color: 'primary.contrastText',
                    border: '1px solid',
                    borderColor: 'text.highlight',
                  }}
                />
              </Box>
            ) : (
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: 'text.primary',
                }}
              >
                {row.productName || '—'}
              </Typography>
            )}
          </>
        )
      }
      if (id === 'payer' || id === 'accountUsed') {
        return <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{(row as any)[id] ?? '—'}</Typography>
      }
      return <Typography sx={{ fontSize: 13 }}>{(row as any)[id] ?? '—'}</Typography>
  }
}
